import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

/**
 * Member properties — canonical routes:
 *   - `GET  /public/properties`                 searchable estate locations (claim picker)
 *   - `GET  /public/property-relationship-types`  ownership types + required documents
 *   - `GET  /public/me/property-claims`           member's claims (enriched)
 *   - `POST /public/me/property-claims`           submit claim
 *   - `POST /public/me/property-claims/:id/cancel`
 *   - `POST /public/me/onboarding-documents`      upload supporting document
 */

export type PropertyResidencyKind = 'resident' | 'non_resident' | 'temporary_resident';

/** Approved property linked to the member (for pickers elsewhere in the app). */
export interface MemberProperty {
  property_id: string;
  unit_label: string;
  address?: string;
  type_name?: string;
  estate_name?: string;
  status?: PropertyClaim['status'] | 'active';
  region?: string;
  thumbnail_url?: string;
}

export interface EstatePropertyOption {
  location_id: string;
  name: string;
  address?: string;
  type_name?: string;
}

export interface RequiredClaimDocument {
  document_type_id: string;
  name: string;
}

export type ResidentFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'select'
  | 'multiselect';

/** Resident-facing custom field defined on a relationship type. */
export interface ResidentCustomField {
  id: string;
  key: string;
  label: string;
  type: ResidentFieldType;
  required: boolean;
  help_text?: string;
  options?: Array<{ label: string; value: string }>;
  config?: { currency_code?: string; min?: number; max?: number; max_length?: number };
}

export interface PropertyRelationshipTypeOption {
  relationship_type_id: string;
  code: string;
  label: string;
  required_documents: RequiredClaimDocument[];
  custom_fields: ResidentCustomField[];
}

export interface PropertyClaim {
  claim_id: string;
  location_id: string;
  property_id: string;
  unit_label: string;
  estate_name?: string;
  address?: string;
  type_name?: string;
  relationship_label?: string;
  property_residency_kind?: PropertyResidencyKind;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  submitted_at: string;
  rejection_reason?: string;
  stand_number?: string;
  photo_url?: string;
  /** Approved AND inside its occupancy window. An approved claim starting next month is not live. */
  is_live: boolean;
  start_date?: string;
  end_date?: string;
}

export interface SubmitPropertyClaimPayload {
  location_id: string;
  property_relationship_type_id?: string;
  property_residency_kind: PropertyResidencyKind;
  evidence?: Record<string, unknown>;
  custom_values?: Record<string, unknown>;
}

interface RawEstateLocation {
  location_id: string;
  name?: string | null;
  address?: string | null;
  type_name?: string | null;
}

interface RawClaimRow {
  claim_id: string;
  location_id: string;
  property_relationship_type_id?: string;
  property_residency_kind?: PropertyResidencyKind;
  verification_status: string;
  rejection_reason?: string | null;
  created_at: string | Date;
  location_name?: string | null;
  location_address?: string | null;
  location_stand_number?: string | null;
  location_photo_url?: string | null;
  type_name?: string | null;
  relationship_label?: string | null;
  is_live?: boolean;
  start_date?: string | null;
  end_date?: string | null;
}

interface RawRelationshipType {
  relationship_type_id: string;
  code: string;
  label: string;
  required_documents?: RequiredClaimDocument[];
  custom_fields?: ResidentCustomField[];
}

function singleLineAddress(address?: string | null): string {
  if (!address) return '';
  return address
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join(', ');
}

function mapLocation(row: RawEstateLocation): EstatePropertyOption {
  const address = singleLineAddress(row.address);
  return {
    location_id: row.location_id,
    name: row.name?.trim() || address || 'Property',
    address: address || undefined,
    type_name: row.type_name?.trim() || undefined,
  };
}

function mapClaim(row: RawClaimRow): PropertyClaim {
  const address = singleLineAddress(row.location_address);
  const status = (row.verification_status || 'pending') as PropertyClaim['status'];
  const standNumber = row.location_stand_number?.trim() || undefined;
  return {
    claim_id: row.claim_id,
    location_id: row.location_id,
    property_id: row.location_id,
    // The stand number is what a resident calls their property out loud, so it leads. The old
    // chain stays as the fallback for properties that carry no stand number.
    unit_label: standNumber ? `Stand ${standNumber}` : row.location_name?.trim() || address || 'Property',
    stand_number: standNumber,
    photo_url: row.location_photo_url?.trim() || undefined,
    is_live: row.is_live ?? status === 'approved',
    start_date: row.start_date ?? undefined,
    end_date: row.end_date ?? undefined,
    address: address || undefined,
    type_name: row.type_name?.trim() || undefined,
    relationship_label: row.relationship_label?.trim() || undefined,
    property_residency_kind: row.property_residency_kind,
    status,
    submitted_at:
      row.created_at instanceof Date ? row.created_at.toISOString() : String(row.created_at),
    rejection_reason: row.rejection_reason ?? undefined,
  };
}

/** The server searches properties from three characters and finds nothing for a shorter term. */
export const PROPERTY_SEARCH_MIN_LENGTH = 3;

export async function searchEstateProperties(
  search: string,
  page = 1,
): Promise<{ properties: EstatePropertyOption[]; pages: number }> {
  const { data: body } = await tenantApi.get('/public/properties', {
    params: { search: search.trim() || undefined, page, limit: 30 },
  });
  const envelope = body as { data?: RawEstateLocation[]; pagination?: { pages?: number } };
  return {
    properties: unwrapList<RawEstateLocation>(envelope).map(mapLocation),
    pages: envelope.pagination?.pages ?? 1,
  };
}

export async function fetchRelationshipTypes(): Promise<PropertyRelationshipTypeOption[]> {
  const { data } = await tenantApi.get('/public/property-relationship-types');
  return unwrapList<RawRelationshipType>(data).map((row) => ({
    relationship_type_id: row.relationship_type_id,
    code: row.code,
    label: row.label,
    required_documents: row.required_documents ?? [],
    custom_fields: Array.isArray(row.custom_fields) ? row.custom_fields : [],
  }));
}

export async function fetchMyClaims(): Promise<PropertyClaim[]> {
  const { data } = await tenantApi.get('/public/me/property-claims');
  return unwrapList<RawClaimRow>(data).map(mapClaim);
}

/**
 * Properties the member actually occupies today: approved AND inside the occupancy window. A claim
 * approved for a move-in next month is not one of these yet.
 */
export function approvedPropertiesFromClaims(claims: PropertyClaim[]): PropertyClaim[] {
  return claims.filter((c) => c.is_live);
}

/** Approved, but not started yet. Shown separately so a future move-in is visible, not missing. */
export function upcomingPropertiesFromClaims(claims: PropertyClaim[]): PropertyClaim[] {
  return claims.filter((c) => c.status === 'approved' && !c.is_live);
}

/** Member's approved properties — used by support, access invites, welcome flows. */
export async function fetchMyProperties(): Promise<MemberProperty[]> {
  const claims = await fetchMyClaims();
  return approvedPropertiesFromClaims(claims).map((c) => ({
    property_id: c.property_id,
    unit_label: c.unit_label,
    address: c.address,
    type_name: c.type_name,
    estate_name: c.estate_name,
    status: c.status,
  }));
}

export async function submitPropertyClaim(payload: SubmitPropertyClaimPayload): Promise<PropertyClaim> {
  const { data } = await tenantApi.post('/public/me/property-claims', payload);
  return mapClaim(unwrap<RawClaimRow>(data));
}

export async function cancelPropertyClaim(claimId: string): Promise<void> {
  await tenantApi.post(`/public/me/property-claims/${claimId}/cancel`);
}

export async function uploadClaimDocument(
  file: File | Blob,
  documentTypeId: string,
  options: { title?: string; relationshipTypeId?: string; fileName?: string },
): Promise<void> {
  const form = new FormData();
  const name = options.fileName ?? (file instanceof File ? file.name : 'document.pdf');
  form.append('file', file, name);
  form.append('document_type_id', documentTypeId);
  form.append('title', options.title ?? name);
  await tenantApi.post('/public/me/onboarding-documents', form, {
    params: options.relationshipTypeId
      ? { property_relationship_type_id: options.relationshipTypeId }
      : undefined,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/* ------------------------------------------------------------------ */
/* Property detail                                                     */
/* ------------------------------------------------------------------ */

export interface PropertyClassification {
  type_name: string;
  name: string;
}

export interface PropertyDetail {
  location_id: string;
  stand_number: string | null;
  name: string;
  address: string | null;
  type_name: string;
  stand_size: string | null;
  building_size: string | null;
  photo_url: string | null;
  latitude: string | null;
  longitude: string | null;
  classifications: PropertyClassification[];
  /** Drives whether the resident may move the map pin. */
  is_owner: boolean;
}

export interface PropertyCustomFieldValue {
  attribute_id: string;
  label: string;
  attribute_key: string;
  data_type: string;
  options: string[] | null;
  is_required: boolean;
  editable: boolean;
  value: string | null;
}

export async function fetchPropertyDetail(locationId: string): Promise<PropertyDetail> {
  const { data } = await tenantApi.get(`/public/me/properties/${locationId}`);
  return unwrap<PropertyDetail>(data);
}

export async function fetchPropertyCustomFields(locationId: string): Promise<PropertyCustomFieldValue[]> {
  const { data } = await tenantApi.get(`/public/me/properties/${locationId}/custom-fields`);
  return unwrapList<PropertyCustomFieldValue>(data);
}

export async function savePropertyCustomFields(
  locationId: string,
  values: Record<string, unknown>,
): Promise<void> {
  await tenantApi.patch(`/public/me/properties/${locationId}/custom-fields`, { values });
}

/** Owners only. The API refuses this for any other relationship type. */
export async function updatePropertyCoordinates(
  locationId: string,
  latitude: number,
  longitude: number,
): Promise<void> {
  await tenantApi.patch(`/public/me/properties/${locationId}/location`, { latitude, longitude });
}

export interface PropertyDocument {
  document_id: string;
  title: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  category: string | null;
  created_at: string;
}

export async function fetchPropertyDocuments(locationId: string): Promise<PropertyDocument[]> {
  const { data } = await tenantApi.get(`/public/me/properties/${locationId}/documents`);
  const body = data as { data?: { documents?: PropertyDocument[] } };
  return body.data?.documents ?? [];
}

export async function downloadPropertyDocument(
  locationId: string,
  documentId: string,
): Promise<Blob> {
  const { data } = await tenantApi.get(
    `/public/me/properties/${locationId}/documents/${documentId}/download`,
    { responseType: 'blob' },
  );
  return data as Blob;
}
